if (!customElements.get("redirection-popup")) {
  customElements.define(
    "redirection-popup",
    class RedirectionPopup extends HTMLElement {
      constructor() {
        super();
        this.cookieName = "localePopup";
        this.storeUrl = this.dataset.requestOrigin;
        this.usStoreUrl = this.dataset.usUrl;
        this.ukStoreUrl = this.dataset.ukUrl;
        this.euStoreUrl = this.dataset.euUrl;
        this.caStoreUrl = this.dataset.caUrl;
        this.auStoreUrl = this.dataset.auUrl;
        this.globalStoreUrl = this.dataset.globalUrl || this.usStoreUrl;

        this.currStoreUrl = window.shopUrl;
        this.currStoreCountry = window.Shopify.country;

        this.closeButton = this.querySelector(".redirection-popup-close");
        this.countryName = this.querySelector(".user-country-name");
        this.redirectButton = this.querySelector("#locale-redirect-to");
        this.redirectAlternateButton = this.querySelector("#locale-redirect-to-alternate");
        this.stayButton = this.querySelector("#locale-stay");

        this.redirectionStoreFlag = this.querySelector("#locale-redirect-to .flag-icon");

        this.userLocale = "";
        this.userCountryName = "";

        // List of EU country codes
        this.euCountryCodes = ["AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR",
                               "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL",
                               "PL", "PT", "RO", "SK", "SI", "ES", "SE"];

        this.storeUrls = {
          US: this.usStoreUrl,
          GB: this.ukStoreUrl,
          EU: this.euStoreUrl,
          CA: this.caStoreUrl,
          AU: this.auStoreUrl,
          GLOBAL: this.globalStoreUrl
        };

        this.storeAbbreviations = {
          US: "US",
          GB: "UK",
          EU: "EU",
          CA: "CA",
          AU: "AU"
        };

        this.storeNames = {
          US: "United States",
          GB: "United Kingdom",
          EU: "Europe",
          CA: "Canada",
          AU: "Australia"
        };

        this.init();
      }

      connectedCallback() {
        this.setupEventListeners();
      }

      setupEventListeners() {
        this.redirectButton.addEventListener("click", (e) => this.handleRedirect(e));
        this.redirectAlternateButton.addEventListener("click", (e) => this.handleRedirect(e));
        this.stayButton.addEventListener("click", () => this.handleStay());
        this.closeButton.addEventListener("click", () => this.handleStay());
      }

      handleRedirect(e) {
        this.setCookie(this.cookieName, "shown", 60);
      }

      handleStay() {
        this.setCookie(this.cookieName, "shown", 60);
        this.close();
      }

      async init() {
        if (this.getCookie(this.cookieName) == "shown") return;
        await this.checkRegion();
        if (!this.shouldShowPopup()) return;
        this.updatePopupContent();
        this.open();
        // Disable interaction until initialisation is complete
        setTimeout(() => {
          this.querySelector(".redirection-popup-wrapper").style.pointerEvents = "";
        }, 100);
      }

      async checkRegion() {
        try {
          const response = await fetch("/browsing_context_suggestions.json");
          const responseJson = await response.json();
          this.userLocale = responseJson?.detected_values?.country?.handle;
          this.userCountryName = responseJson?.detected_values?.country_name;
        } catch (error) {
          console.error("Error fetching user region:", error);
        }
      }

      getRedirectUrl(countryCode) {
        if (countryCode === "US") return this.storeUrls.US;
        if (countryCode === "GB") return this.storeUrls.GB;
        if (countryCode === "CA") return this.storeUrls.CA;
        if (countryCode === "AU") return this.storeUrls.AU;
        if (this.euCountryCodes.includes(countryCode)) return this.storeUrls.EU;
        return this.storeUrls.GLOBAL;
      }

      getVisitorLocation() {
        const redirectURL = this.getRedirectUrl(this.userLocale);
        const location = Object.keys(this.storeUrls).find(key => this.storeUrls[key] === redirectURL) || "GLOBAL"
        return location;
      }

      getCurrentStore() {
        const currentOrigin = new URL(this.currStoreUrl).origin;
        return Object.keys(this.storeUrls).find(key => new URL(this.storeUrls[key]).origin === currentOrigin) || "GLOBAL";
      }

      getVisitRedirectObject() {
        const currentStore = this.getCurrentStore();
        const visitorLocation = this.getVisitorLocation();
    
        let object = {
          redirect_to: { url: '', text: '', abbreviation: '' },
          redirect_to_alternate: { url: '', text: '', abbreviation: '' },
          stay: {
            url: this.currStoreUrl,
            text: this.storeNames[currentStore] || "Global Store",
            abbreviation: this.storeAbbreviations[currentStore] || "Global"
          },
        };

        const redirectPriority = ["US", "GB", "EU", "CA", "AU"].filter(store => store !== currentStore);

        object.redirect_to = this.getRedirectInfo(visitorLocation, redirectPriority[0]);
        object.redirect_to_alternate = this.getRedirectInfo(visitorLocation, redirectPriority[1]);

        return object;
      }

      getRedirectInfo(visitorLocation, defaultStore) {
        const store = (visitorLocation !== "GLOBAL") ? visitorLocation : defaultStore;
        return {
          url: this.storeUrls[store],
          text: this.storeNames[store],
          abbreviation: this.storeAbbreviations[store]
        };
      }

      updatePopupContent() {
        const redirectObject = this.getVisitRedirectObject();

        this.countryName.innerText = this.userCountryName || "your country";

        this.updateButtonContent(this.stayButton, redirectObject.stay);
        this.updateButtonContent(this.redirectButton, redirectObject.redirect_to);
        this.updateButtonContent(this.redirectAlternateButton, redirectObject.redirect_to_alternate);
      }

      updateButtonContent(button, content) {
        button.setAttribute("href", content.url);
        const textElement = button.querySelector("[id^='country']") || button;
        textElement.innerText = content.abbreviation;
      }

      shouldShowPopup() {
        const expectedRedirectUrl = new URL(this.getRedirectUrl(this.userLocale)).origin;
        const currentStoreUrl = new URL(this.currStoreUrl).origin;
        return this.userLocale && !currentStoreUrl.startsWith(expectedRedirectUrl);
      }

      open() {
        this.classList.add("show");
      }

      close() {
        this.classList.remove("show");
      }

      setCookie(cName, cValue, expDays) {
        const date = new Date();
        date.setTime(date.getTime() + (expDays * 24 * 60 * 60 * 1000));
        const expires = "expires=" + date.toUTCString();
        document.cookie = `${cName}=${cValue}; ${expires}; path=/`;
      }

      getCookie(cookieName) {
        const cookies = document.cookie.split(";");
        for (let cookie of cookies) {
          const [name, value] = cookie.trim().split("=");
          if (name === cookieName) return decodeURIComponent(value);
        }
        return null;
      }
    }
  );
}
